import { getLinkPath, getI18nData } from '@lucid/utils';
import { graphql, useStaticQuery } from 'gatsby';
import { LayoutContextProps } from '@lucid/components-react';

interface MarketoLink {
  url: string;
  copy: string;
}

const useMarketoLink = (
  language: Pick<LayoutContextProps['language'], 'i18nKey' | 'langPrefix'>
) => {
  const { drupal, i18nData } = useStaticQuery<any>(
    graphql`
      {
        drupal {
          quote: nodeById(id: "4045") {
            entityUrl {
              ...getLinkPath
            }
          }
          demo: nodeById(id: "4048") {
            entityUrl {
              ...getLinkPath
            }
          }
        }
        i18nData: allFile(filter: { sourceInstanceName: { eq: "i18n" } }) {
          edges {
            node {
              name
              childSrcJson {
                mktg_ct_all_get_a_quote
                mktg_ct_all_request_a_demo
              }
            }
          }
        }
      }
    `
  );

  const i18n = getI18nData(i18nData, language.i18nKey);

  const textDefaults: Record<string, string> = {
    quote: i18n('mktg-ct-all-get-a-quote'),
    demo: i18n('mktg-ct-all-request-a-demo')
  };

  const keyToData: Record<string, MarketoLink> = {};

  Object.keys(drupal).forEach(key => {
    keyToData[key] = {
      url: getLinkPath(drupal[key].entityUrl, language.langPrefix),
      copy: textDefaults[key]
    };
  });

  const data = {
    getAQuote: keyToData['quote'],
    requestADemo: keyToData['demo']
  };

  const defaultValue = data.getAQuote;

  const singleMarketoFormPages = new Map([
    // single page get a quote
    ['1120', data.getAQuote],
    ['1138', data.getAQuote],
    ['1984', data.getAQuote],
    ['1980', data.getAQuote],
    ['1992', data.getAQuote],
    ['1996', data.getAQuote],
    ['2166', data.getAQuote],
    ['1988', data.getAQuote],
    // legacy get a quote
    ['1055', data.getAQuote],
    ['1395', data.getAQuote],
    ['1261', data.getAQuote],
    ['1266', data.getAQuote],
    ['1263', data.getAQuote],
    ['1269', data.getAQuote],
    ['1366', data.getAQuote],
    ['2015', data.getAQuote],
    ['1264', data.getAQuote],
    // enterprise get a quote - top
    ['1127', data.getAQuote],
    // enterprise get a quote - bottom
    ['1128', data.getAQuote],
    ['1982', data.getAQuote],
    ['1978', data.getAQuote],
    ['1979', data.getAQuote],
    ['1990', data.getAQuote],
    ['1994', data.getAQuote],
    ['2162', data.getAQuote],
    ['1986', data.getAQuote],
    ['1987', data.getAQuote],
    ['1943', data.getAQuote],
    // edu get a quote
    ['1056', data.getAQuote],
    ['1265', data.getAQuote],
    // nineteen-hero get a quote
    ['2145', data.getAQuote],
    ['3175', data.getAQuote],
    // request a demo
    ['1060', data.requestADemo],
    ['1394', data.requestADemo],
    ['1270', data.requestADemo],
    ['1384', data.requestADemo],
    ['1379', data.requestADemo],
    ['1357', data.requestADemo],
    ['2014', data.requestADemo],
    ['1364', data.requestADemo],
    // demo request pages request a demo
    ['2383', data.requestADemo],
    // nineteen-hero request a demo
    ['2601', data.requestADemo],
    ['2617', data.requestADemo],
    ['3176', data.requestADemo]
  ]);

  return (marketoId?: string | null) => {
    let value = defaultValue;

    if (marketoId) {
      if (!singleMarketoFormPages.has(marketoId)) {
        throw new Error(`Invalid Marketo Id: ${marketoId}`);
      }
      value = singleMarketoFormPages.get(marketoId)!;
    }

    return value;
  };
};

export default useMarketoLink;
